<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { useInsightConfigList } from '../../../composables/useInsightConfigList'
import type { InsightConfig } from '../../../models/insightConfig.types'

import { configColumnI18nKey } from './configI18n'

import { ReportTable, type ReportColumn, type ReportRow } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Alert } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { useConfirm } from '@/shared/ui/feedback'
import { Input } from '@/shared/ui/input'
import { PageHeader } from '@/shared/ui/page-header'
import { Select } from '@/shared/ui/select'

const props = defineProps<{ createRouteName: string; editRouteName: string }>()
const { t } = useI18n()
const format = useFormat()
const router = useRouter()
const confirm = useConfirm()
const list = useInsightConfigList()
const rows = computed<ReportRow[]>(() =>
  list.configs.value.flatMap((config) =>
    config.id === undefined
      ? []
      : [
          {
            id: config.id,
            enum: config.enum,
            label: config.label,
            status: config.status,
            outcome_category: config.outcome_category,
            severity: config.severity,
            confidence: config.confidence,
            platform: Array.isArray(config.platform) ? config.platform.join(', ') : config.platform,
            content_type: config.content_type,
            lookback_days: config.lookback_days,
            default_refresh_interval_seconds: config.default_refresh_interval_seconds,
            priority: config.priority,
            insights_count: config.insights_count ?? 0,
            actions: '',
          },
        ],
  ),
)
const columns = computed<ReportColumn[]>(() =>
  [
    'enum',
    'label',
    'status',
    'outcome_category',
    'severity',
    'confidence',
    'platform',
    'content_type',
    'lookback_days',
    'default_refresh_interval_seconds',
    'priority',
    'insights_count',
    'actions',
  ].map((field) => ({
    field,
    header: t(`insights.sysadmin.config.columns.${configColumnI18nKey(field)}`),
    numeric: [
      'lookback_days',
      'default_refresh_interval_seconds',
      'priority',
      'insights_count',
    ].includes(field),
  })),
)
const numericFields = new Set([
  'lookback_days',
  'default_refresh_interval_seconds',
  'priority',
  'insights_count',
])

async function remove(config: InsightConfig): Promise<void> {
  if (config.id === undefined) return
  const accepted = await confirm({
    title: t('insights.sysadmin.config.deleteTitle'),
    message: t('insights.sysadmin.config.deleteConfirm'),
    confirmLabel: t('insights.sysadmin.config.delete'),
    cancelLabel: t('insights.sysadmin.config.cancel'),
    danger: true,
  })
  if (accepted) await list.deleteConfig(config.id)
}
function rowId(row: ReportRow): number {
  return typeof row['id'] === 'number' ? row['id'] : 0
}
function openRow(row: ReportRow): void {
  void router.push({ name: props.editRouteName, params: { configId: rowId(row) } })
}
</script>

<template>
  <main class="config-list grid gap-lg" data-testid="insight-config-list">
    <PageHeader
      :title="t('insights.sysadmin.config.listTitle')"
      :subtitle="t('insights.sysadmin.config.listSubtitle')"
    >
      <template #actions>
        <Button @click="router.push({ name: createRouteName })">
          {{ t('insights.sysadmin.config.create') }}
        </Button>
      </template>
    </PageHeader>
    <form
      class="config-list__filters grid grid-cols-[repeat(5,_minmax(8rem,_1fr))] gap-sm items-end max-lg:grid-cols-2"
      @submit.prevent="list.applyFilters"
    >
      <Input
        v-model="list.draftFilters.value.search"
        type="search"
        :label="t('insights.sysadmin.filters.search')"
      />
      <Select
        v-if="list.meta.value"
        v-model="list.draftFilters.value.status"
        :label="t('insights.sysadmin.filters.status')"
        :options="[
          { value: '', label: t('insights.sysadmin.filters.all') },
          ...(list.meta.value.status_choices ?? []),
        ]"
      />
      <Select
        v-if="list.meta.value"
        v-model="list.draftFilters.value.outcome"
        :label="t('insights.sysadmin.filters.outcome')"
        :options="[
          { value: '', label: t('insights.sysadmin.filters.all') },
          ...(list.meta.value.outcome_category_choices ?? []),
        ]"
      />
      <Select
        v-if="list.meta.value"
        v-model="list.draftFilters.value.severity"
        :label="t('insights.sysadmin.filters.severity')"
        :options="[
          { value: '', label: t('insights.sysadmin.filters.all') },
          ...(list.meta.value.severity_choices ?? []),
        ]"
      />
      <Input
        v-model="list.draftFilters.value.platform"
        :label="t('insights.sysadmin.filters.platform')"
      />
      <div class="config-list__filter-actions flex items-center justify-end gap-xs">
        <Button type="submit" size="sm">{{ t('insights.sysadmin.filters.apply') }}</Button>
        <Button type="button" size="sm" variant="ghost" @click="list.clearFilters">
          {{ t('insights.sysadmin.filters.clear') }}
        </Button>
      </div>
    </form>
    <Alert v-if="list.deleteBlockedCount.value > 0" tone="warning">
      {{ t('insights.sysadmin.config.deleteBlocked', { count: list.deleteBlockedCount.value }) }}
    </Alert>
    <Alert v-if="list.state.value === 'idle'" tone="warning">
      {{ t('insights.sysadmin.config.forbidden') }}
    </Alert>
    <ReportTable
      v-else
      :rows="rows"
      :columns="columns"
      :state="list.state.value"
      v-bind="{ ariaLabel: t('insights.sysadmin.config.tableLabel') }"
    >
      <template #empty>{{ t('insights.sysadmin.empty.noConfigs') }}</template>
      <template #error>
        <Alert tone="danger">
          {{ t('insights.sysadmin.config.loadError') }}
          <Button size="xs" variant="ghost" @click="list.load">
            {{ t('insights.error.retry') }}
          </Button>
        </Alert>
      </template>
      <template v-for="field in numericFields" :key="field" #[`cell-${field}`]="{ value }">
        {{ format.formatNumber(Number(value)) }}
      </template>
      <template #cell-actions="{ row }">
        <Button type="button" size="xs" variant="ghost" @click="openRow(row)">
          {{ t('common.edit') }}
        </Button>
        <Button
          type="button"
          size="xs"
          variant="danger"
          @click.stop="remove(list.configs.value.find((config) => config.id === rowId(row))!)"
        >
          {{ t('insights.sysadmin.config.delete') }}
        </Button>
      </template>
    </ReportTable>
    <footer class="config-list__pagination flex items-center justify-end gap-xs">
      <span>{{ format.formatNumber(list.total.value) }}</span>
      <Button :disabled="list.offset.value === 0" variant="ghost" @click="list.previousPage">
        {{ t('insights.sysadmin.pagination.previous') }}
      </Button>
      <Button
        :disabled="list.offset.value + 20 >= list.total.value"
        variant="ghost"
        @click="list.nextPage"
      >
        {{ t('insights.sysadmin.pagination.next') }}
      </Button>
    </footer>
  </main>
</template>
